// Section: standard section wrapper.
// Composes <section className="section wrap"> + optional SecHead.
//
// Replaces the 12+ inline `<section className="section wrap" id="..."><SecHead ...>`
// patterns across the codebase.

import type { ReactNode } from 'react';
import { SecHead, type SecHeadLink } from './SecHead';
import { cx } from '@lib/utils/classNames';

export interface SectionProps {
  children: ReactNode;
  /** Section id for hash navigation. */
  id?: string;
  /** Adds the --tight modifier (smaller vertical padding). */
  tight?: boolean;
  /** Drops the .wrap container (full-bleed). */
  fullBleed?: boolean;
  className?: string;

  // SecHead props — pass these to render the heading at the top of the section.
  eyebrow?: string;
  title?: string;
  lead?: string;
  link?: SecHeadLink;
  magenta?: boolean;
}

export function Section({
  children,
  id,
  tight,
  fullBleed,
  className,
  eyebrow,
  title,
  lead,
  link,
  magenta,
}: SectionProps) {
  const sectionClass = cx(
    'section',
    tight && 'section--tight',
    !fullBleed && 'wrap',
    className
  );

  const hasHeading = Boolean(eyebrow && title);

  return (
    <section className={sectionClass} id={id}>
      {hasHeading && (
        <SecHead
          eyebrow={eyebrow!}
          title={title!}
          {...(lead !== undefined ? { lead } : {})}
          {...(link !== undefined ? { link } : {})}
          {...(magenta !== undefined ? { magenta } : {})}
        />
      )}
      {children}
    </section>
  );
}
